/**
 * Demo scheduling for ?mock=1 over the shared mock fixture, and a small in-browser stand-in for
 * the hub's scheduler API so every control works without a hub. Validation comes from
 * @orgus/protocol, the same functions the hub uses (apps/hub/src/ext/scheduler).
 */
import { DEFAULT_SCHEDULER_SETTINGS, cleanLabels, enqueueError, limitError, poolInputError, timeRuleError, ulid } from '@orgus/protocol';
import type { AgentBreaker, AgentEnvironmentReport, AgentHealth, AgentUsage, RuntimeFault, SelfTestResult, EnqueueRequest, Id, Mission, NodePool, NodeSettings, PoolInput, PoolInstance, ProviderInfo, QueueEntry, SchedulerSettings, SchedulerState, TimeRule } from '@orgus/protocol';
import { MOCK_IDS } from '../../lib/mock.ts';

const MIN = 60_000;
const H = 60 * MIN;

export class MockRefusal extends Error {}

/** The hub's built-in fake provider, as the console sees it. */
export const FAKE_PROVIDER: ProviderInfo = {
  id: 'fake',
  label: 'Fake (simulated, no cloud calls)',
  real: false,
  sizes: [
    { id: 'small', label: 'Small: 2 CPUs, 4 GB', cpus: 2, memGb: 4 },
    { id: 'medium', label: 'Medium: 4 CPUs, 16 GB', cpus: 4, memGb: 16 },
    { id: 'large', label: 'Large: 8 CPUs, 32 GB', cpus: 8, memGb: 32 },
    { id: 'gpu', label: 'GPU: 8 CPUs, 64 GB, 1 GPU', cpus: 8, memGb: 64 },
  ],
  images: ['orgus-agent-ubuntu-24.04', 'orgus-agent-debian-12'],
};

function entry(id: string, missionId: Id, projectId: Id, title: string, extra: Partial<QueueEntry>, now: number): QueueEntry {
  return {
    id,
    missionId,
    projectId,
    title,
    person: 'owner',
    by: 'owner',
    needs: { labels: [], runtime: 'auto', minFreeMemGb: null },
    machineId: null,
    start: { mode: 'headless', model: null, permissionMode: null, newWorktree: null, note: null },
    priority: 2,
    state: 'queued',
    enqueuedAt: now - 20 * MIN,
    updatedAt: now - 20 * MIN,
    attempts: 0,
    reason: null,
    message: null,
    placedOn: null,
    checkoutId: null,
    startedAt: null,
    launchId: null,
    sessionId: null,
    ...extra,
  };
}

export function createMockScheduler(now: number = Date.now()): SchedulerState {
  const M = MOCK_IDS;
  const rules: TimeRule[] = [{ id: 'rul_demo_laptop', label: 'laptop', from: '22:00', to: '07:00', days: [1, 2, 3, 4, 5], note: 'Keep the laptop cool overnight', createdBy: 'owner', createdAt: now - 48 * H }];
  const settings: SchedulerSettings = {
    ...DEFAULT_SCHEDULER_SETTINGS,
    limits: { perProject: null, perPerson: 8 },
    projects: { [M.billing]: { offline: 'wait', maxSessions: 4 }, [M.docs]: { offline: 'reschedule', maxSessions: null } },
    rules,
  };
  const nodes: Record<Id, NodeSettings> = {
    [M.mbp]: { machineId: M.mbp, labels: ['laptop', 'travel'], maxSessions: 6, drain: null },
    [M.build]: { machineId: M.build, labels: null, maxSessions: null, drain: { at: now - 3 * H - 20 * MIN, by: 'owner', reason: 'Kernel update' } },
  };
  const pool: NodePool = { id: 'npl_demo_gpu', name: 'gpu-linux', provider: 'fake', image: 'orgus-agent-ubuntu-24.04', size: 'gpu', labels: ['linux', 'gpu'], min: 0, max: 2, idleTimeoutMin: 15, createdBy: 'owner', createdAt: now - 26 * H };
  const instances: PoolInstance[] = [
    { id: 'nin_demo_1', poolId: pool.id, provider: 'fake', ref: 'fake-1', name: 'gpu-linux-7k2m9q', state: 'provisioning', machineId: null, slots: 4, createdAt: now - 2 * MIN, updatedAt: now - 2 * MIN, idleSince: null, message: 'Simulated by the fake provider: no real machine will join.' },
    { id: 'nin_demo_0', poolId: pool.id, provider: 'fake', ref: 'fake-0', name: 'gpu-linux-3hx81c', state: 'gone', machineId: null, slots: 4, createdAt: now - 5 * H, updatedAt: now - 4 * H, idleSince: null, message: 'Idle for 15 min' },
  ];
  const queue: QueueEntry[] = [
    entry('que_demo_rank', 'msn_demo_rank', M.qawl, 'Retrain the ranking model on last week', { priority: 1, needs: { labels: ['gpu', 'linux'], runtime: 'auto', minFreeMemGb: 24 }, enqueuedAt: now - 6 * MIN }, now),
    entry('que_demo_deps', 'msn_demo_deps', M.billing, 'Nightly dependency upgrade', { priority: 3, machineId: M.build, enqueuedAt: now - 3 * H }, now),
    // ADR 0049 decision 5: Codex hit its usage limit, so the mission moved to Claude Code on the same branch.
    entry('que_demo_openapi', 'msn_demo_openapi', M.docs, 'API reference from the OpenAPI spec', {
      state: 'started',
      needs: { labels: [], runtime: 'auto', avoid: ['codex'], minFreeMemGb: null },
      start: { mode: 'headless', model: null, permissionMode: null, newWorktree: null, note: 'Codex failed (usage limit). The work continues on the same branch.' },
      placedOn: M.mbp,
      agentKind: 'claude-code',
      agentReason: '4 of 5 missions done on this project; about $0.42 a mission',
      switches: 1,
      failovers: [{ kind: 'codex', machineId: M.mbp, cause: 'usage-limit', summary: 'The usage limit was reached; it resets 6pm.', at: now - 15 * MIN }],
      startedAt: now - 14 * MIN,
      updatedAt: now - 14 * MIN,
      attempts: 1,
      message: 'Started on mbp-16.',
    }, now),
    entry('que_demo_search', 'msn_demo_search', M.docs, 'Search across the docs', { state: 'failed', placedOn: M.mbp, agentKind: 'claude-code', startedAt: now - 2 * H, updatedAt: now - 2 * H, attempts: 1, message: 'claude was not found on PATH. Install Claude Code on mbp-16, then retry.' }, now),
  ];
  const health: AgentHealth[] = [{ machineId: M.mbp, kind: 'codex', coolUntil: now + 10 * MIN, strikes: 1, lastCause: 'usage-limit', lastMessage: 'The usage limit was reached; it resets 6pm.', at: now - 15 * MIN }];
  // Claude Code's subscription windows on the laptop, as its /usage probe last read them.
  const usage: AgentUsage[] = [
    {
      agentKind: 'claude-code',
      machineId: M.mbp,
      status: 'warning',
      source: 'probe',
      at: now - 4 * MIN,
      windows: [
        { kind: 'session', label: 'Current session', percent: 68, resetsAt: now + 2 * H + 10 * MIN, severity: 'normal' },
        { kind: 'weekly_all', label: 'Weekly, all models', percent: 83, resetsAt: now + 50 * H, severity: 'warning' },
        { kind: 'weekly_scoped', label: 'Weekly, Opus', percent: 41, resetsAt: now + 50 * H, scope: 'Opus', severity: 'normal' },
      ],
    },
    // The demo's Claude Max launch account (ADR 0088), read by a run on it.
    {
      agentKind: 'claude-code',
      machineId: M.mbp,
      accountId: 'la_max',
      status: 'allowed',
      source: 'stream',
      at: now - 11 * MIN,
      windows: [
        { kind: 'session', label: 'Current session', percent: 22, resetsAt: now + 3 * H + 40 * MIN, severity: 'normal' },
        { kind: 'weekly_all', label: 'Weekly, all models', percent: 47, resetsAt: now + 98 * H, severity: 'normal' },
      ],
    },
  ];
  // The laptop's environment check (ADR 0093): Codex sessions find a Git Bash shim first.
  const environment: AgentEnvironmentReport[] = [
    {
      machineId: M.mbp,
      at: now - 6 * MIN,
      platform: 'win32',
      environment: { state: 'read', source: 'registry', readAt: now - 6 * MIN, names: 58 },
      user: [],
      launches: [
        { launch: 'claude-code', resolvers: ['bash'], status: 'match', differences: [] },
        { launch: 'codex', resolvers: ['powershell'], status: 'differs', differences: [{ tool: 'pnpm', resolver: 'powershell', launch: 'C:\\Users\\dev\\bin\\pnpm', user: 'C:\\Users\\dev\\AppData\\Roaming\\npm\\pnpm.cmd', launchNoExtension: true }] },
        { launch: 'acp', resolvers: ['powershell', 'cmd'], status: 'match', differences: [] },
      ],
      status: 'differs',
    },
  ];
  // ADR 0049 decision 12: the build box refuses Codex with its own ceiling, so Orgus never places it there.
  const refused = [{ machineId: M.build, kind: 'codex', entry: 'session.start:agent:codex' }];
  // ADR 0089: Gemini CLI's terminal layer broke on the laptop (node-pty), so its work moved to Claude Code; Codex passed its last self-test.
  const attach: RuntimeFault = { signature: 'attach-console', label: 'The terminal layer failed (AttachConsole failed)', key: 'attach-console', count: 4, excerpt: 'Error: AttachConsole failed\n    at Object.<anonymous> (...\\@lydell\\node-pty\\conpty_console_list_agent.js:11:26)', command: null, source: 'stderr', firstAt: now - 42 * MIN, at: now - 38 * MIN };
  const geminiTest: SelfTestResult = { requestId: 'stt_demo_gemini', machineId: M.mbp, kind: 'gemini-cli', passed: false, failedStep: 'runtime', steps: [{ step: 'start', ok: true, evidence: 'The session started through session.start.' }, { step: 'shell', ok: true, evidence: 'shell-ok.txt was written.' }, { step: 'runtime', ok: false, evidence: 'The terminal layer failed (AttachConsole failed): Error: AttachConsole failed' }], fault: attach, durationMs: 48_000, at: now - 21 * MIN };
  const breakers: AgentBreaker[] = [
    { machineId: M.mbp, kind: 'gemini-cli', setup: { agentVersion: '0.58.0', orgusVersion: '0.9.0', envFingerprint: 'demo' }, state: 'open', fault: attach, sessionId: null, movedTo: 'claude-code', openedAt: now - 40 * MIN, testAfter: now + 4 * MIN, testing: null, lastTest: geminiTest, trial: null, remedy: null, notifiedAt: now - 40 * MIN, escalatedAt: null, reopened: 1, at: now - 21 * MIN },
  ];
  const selfTests: SelfTestResult[] = [geminiTest, { requestId: 'stt_demo_codex', machineId: M.mbp, kind: 'codex', passed: true, failedStep: null, steps: [], durationMs: 53_000, at: now - 3 * H }];
  return { settings: { ...settings, runtimeBreaker: true }, nodes, queue, pools: [pool], instances, providers: [FAKE_PROVIDER], capacity: [], health, refused, usage, usageProbe: true, environment, breakers, selfTests, at: now };
}

/** Test now in demo mode: the pair tests, then passes a moment later (half-open). */
export function mockTestAgent(s: SchedulerState, machineId: Id, kind: string, now: number): SchedulerState {
  const b = (s.breakers ?? []).find((x) => x.machineId === machineId && x.kind === kind);
  if (b?.state === 'testing') throw new MockRefusal('A self-test runs now.');
  const result: SelfTestResult = { requestId: `stt_demo_${now}`, machineId, kind, passed: true, failedStep: null, steps: [], durationMs: 41_000, at: now };
  return {
    ...s,
    breakers: (s.breakers ?? []).map((x) => (x === b ? { ...x, state: 'half-open', testing: null, lastTest: result, at: now } : x)),
    selfTests: [result, ...(s.selfTests ?? []).filter((x) => !(x.machineId === machineId && x.kind === kind))],
  };
}

export function mockUsageProbe(s: SchedulerState, on: boolean): SchedulerState {
  return { ...s, usageProbe: on };
}

// ---------------------------------------------------------------------------
// The stand-in API. Each returns the next state (and the changed object).
// ---------------------------------------------------------------------------

const newId = (p: string, now: number) => `${p}_${ulid(now)}`;

export function mockEnqueue(s: SchedulerState, body: EnqueueRequest, mission: Mission | undefined, by: string, now: number): { state: SchedulerState; entry: QueueEntry } {
  const err = enqueueError(body);
  if (err) throw new MockRefusal(err);
  if (!mission) throw new MockRefusal('That mission is not known to this hub.');
  if (mission.closed) throw new MockRefusal(`It is closed as ${mission.closed.as}.`);
  if (s.queue.some((e) => e.missionId === mission.id && (e.state === 'queued' || e.state === 'starting'))) throw new MockRefusal('It is already in the queue.');
  const e = entry(newId('que', now), mission.id, mission.projectId, mission.title, {
    person: mission.owner,
    by,
    needs: { labels: cleanLabels(body.needs?.labels ?? mission.placement.labels ?? []), runtime: body.needs?.runtime ?? 'auto', minFreeMemGb: body.needs?.minFreeMemGb ?? null },
    machineId: body.machineId ?? null,
    start: { mode: body.mode ?? 'headless', model: body.model === undefined ? mission.model : body.model, permissionMode: body.permissionMode ?? null, newWorktree: body.newWorktree ?? null, note: body.note?.trim() || null, ...(body.checkoutId ? { checkoutId: body.checkoutId } : {}) },
    priority: body.priority ?? 2,
    enqueuedAt: now,
    updatedAt: now,
  }, now);
  return { state: { ...s, queue: [e, ...s.queue] }, entry: e };
}

export function mockSetEntry(s: SchedulerState, id: Id, patch: Partial<QueueEntry>, now: number): SchedulerState {
  return { ...s, queue: s.queue.map((e) => (e.id === id ? { ...e, ...patch, updatedAt: now } : e)) };
}

export function mockCancel(s: SchedulerState, id: Id, by: string, now: number): SchedulerState {
  const e = s.queue.find((x) => x.id === id);
  if (!e) throw new MockRefusal('That queue entry is not known to this hub.');
  if (e.state !== 'queued') throw new MockRefusal(`It is ${e.state}.`);
  return mockSetEntry(s, id, { state: 'cancelled', reason: null, message: `Cancelled by ${by}.` }, now);
}

export function mockRetry(s: SchedulerState, id: Id, now: number): SchedulerState {
  const e = s.queue.find((x) => x.id === id);
  if (!e) throw new MockRefusal('That queue entry is not known to this hub.');
  if (e.state !== 'failed' && e.state !== 'cancelled') throw new MockRefusal('Only failed or cancelled entries can be queued again.');
  return mockSetEntry(s, id, { state: 'queued', attempts: 0, reason: null, message: null, placedOn: null, checkoutId: null, startedAt: null, enqueuedAt: now }, now);
}

export function mockPatchNode(s: SchedulerState, machineId: Id, patch: { labels?: string[] | null; maxSessions?: number | null }): SchedulerState {
  const prev = s.nodes[machineId] ?? { machineId, labels: null, maxSessions: null, drain: null };
  if ('maxSessions' in patch) {
    const err = limitError(patch.maxSessions, 'The machine limit');
    if (err) throw new MockRefusal(err);
  }
  const next: NodeSettings = {
    ...prev,
    ...('labels' in patch ? { labels: patch.labels === null ? null : cleanLabels(patch.labels) } : {}),
    ...('maxSessions' in patch ? { maxSessions: patch.maxSessions ?? null } : {}),
  };
  return { ...s, nodes: { ...s.nodes, [machineId]: next } };
}

export function mockDrain(s: SchedulerState, machineId: Id, on: boolean, by: string, reason: string, now: number): SchedulerState {
  const prev = s.nodes[machineId] ?? { machineId, labels: null, maxSessions: null, drain: null };
  if (on && prev.drain) throw new MockRefusal('It is already drained.');
  if (!on && !prev.drain) throw new MockRefusal('It already takes new work.');
  return { ...s, nodes: { ...s.nodes, [machineId]: { ...prev, drain: on ? { at: now, by, reason } : null } } };
}

export function mockPatchSettings(s: SchedulerState, patch: Partial<Pick<SchedulerSettings, 'defaultOffline' | 'utcOffsetMinutes' | 'runtimeBreaker'>> & { agents?: { preferred?: string[]; off?: string[]; spare?: string[] }; limits?: Partial<SchedulerSettings['limits']>; headroom?: Partial<SchedulerSettings['headroom']> }): SchedulerState {
  for (const [k, v] of Object.entries(patch.limits ?? {})) {
    const err = limitError(v, k === 'perProject' ? 'The project limit' : 'The person limit');
    if (err) throw new MockRefusal(err);
  }
  return {
    ...s,
    settings: {
      ...s.settings,
      ...(patch.defaultOffline ? { defaultOffline: patch.defaultOffline } : {}),
      ...('utcOffsetMinutes' in patch ? { utcOffsetMinutes: patch.utcOffsetMinutes ?? null } : {}),
      ...(patch.agents
        ? (() => {
            const off = patch.agents.off ?? s.settings.agents?.off ?? [];
            const spare = patch.agents.spare ?? s.settings.agents?.spare ?? [];
            return { agents: { preferred: [...(patch.agents.preferred ?? s.settings.agents?.preferred ?? [])], ...(off.length ? { off: [...off] } : {}), ...(spare.length ? { spare: [...spare] } : {}) } };
          })()
        : {}),
      limits: { ...s.settings.limits, ...patch.limits },
      headroom: { ...s.settings.headroom, ...patch.headroom },
      ...(typeof patch.runtimeBreaker === 'boolean' ? { runtimeBreaker: patch.runtimeBreaker } : {}),
    },
  };
}

export function mockPutProject(s: SchedulerState, projectId: Id, patch: { offline?: 'wait' | 'reschedule'; maxSessions?: number | null }): SchedulerState {
  const err = limitError(patch.maxSessions, 'The project limit');
  if (err) throw new MockRefusal(err);
  const prev = s.settings.projects[projectId] ?? { offline: s.settings.defaultOffline, maxSessions: null };
  return { ...s, settings: { ...s.settings, projects: { ...s.settings.projects, [projectId]: { offline: patch.offline ?? prev.offline, maxSessions: patch.maxSessions === undefined ? prev.maxSessions : patch.maxSessions } } } };
}

export function mockAddRule(s: SchedulerState, body: { label: string; from: string; to: string; days?: number[]; note?: string }, by: string, now: number): { state: SchedulerState; rule: TimeRule } {
  const err = timeRuleError(body);
  if (err) throw new MockRefusal(err);
  const rule: TimeRule = { id: newId('rul', now), label: body.label.trim().toLowerCase(), from: body.from, to: body.to, days: [...new Set(body.days ?? [])].sort((a, b) => a - b), note: (body.note ?? '').trim(), createdBy: by, createdAt: now };
  return { state: { ...s, settings: { ...s.settings, rules: [...s.settings.rules, rule] } }, rule };
}

export function mockDeleteRule(s: SchedulerState, id: Id): SchedulerState {
  return { ...s, settings: { ...s.settings, rules: s.settings.rules.filter((r) => r.id !== id) } };
}

export function mockCreatePool(s: SchedulerState, body: PoolInput, by: string, now: number): { state: SchedulerState; pool: NodePool } {
  const err = poolInputError(body, s.providers);
  if (err) throw new MockRefusal(err);
  if (s.pools.some((p) => p.name.toLowerCase() === body.name.trim().toLowerCase())) throw new MockRefusal(`A pool named ${body.name.trim()} exists.`);
  const pool: NodePool = { id: newId('npl', now), name: body.name.trim(), provider: body.provider, image: body.image.trim(), size: body.size, labels: cleanLabels(body.labels ?? []), min: body.min ?? 0, max: body.max ?? 2, idleTimeoutMin: body.idleTimeoutMin ?? 15, createdBy: by, createdAt: now };
  return { state: { ...s, pools: [...s.pools, pool] }, pool };
}

export function mockUpdatePool(s: SchedulerState, id: Id, patch: Partial<PoolInput>): SchedulerState {
  const err = poolInputError(patch, s.providers, true);
  if (err) throw new MockRefusal(err);
  const prev = s.pools.find((p) => p.id === id);
  if (!prev) throw new MockRefusal('That node pool is not known to this hub.');
  const next = { ...prev, ...patch, ...(patch.labels ? { labels: cleanLabels(patch.labels) } : {}) } as NodePool;
  if (next.min > next.max) throw new MockRefusal('The minimum cannot be above the maximum.');
  // The demo removes machines above a lowered maximum at once, newest first.
  const live = s.instances.filter((i) => i.poolId === id && (i.state === 'provisioning' || i.state === 'running')).sort((a, b) => b.createdAt - a.createdAt);
  const drop = new Set(live.slice(0, Math.max(0, live.length - next.max)).map((i) => i.id));
  return {
    ...s,
    pools: s.pools.map((p) => (p.id === id ? next : p)),
    instances: s.instances.map((i) => (drop.has(i.id) ? { ...i, state: 'gone', message: `Above the pool maximum of ${next.max}` } : i)),
  };
}

export function mockDeletePool(s: SchedulerState, id: Id): SchedulerState {
  const live = s.instances.filter((i) => i.poolId === id && (i.state === 'provisioning' || i.state === 'running')).length;
  if (live) throw new MockRefusal(`It still has ${live} ${live === 1 ? 'machine' : 'machines'}. Set its maximum to 0 first.`);
  return { ...s, pools: s.pools.filter((p) => p.id !== id) };
}
